/* @ngInject */

export default (
  $rootScope,
  helperService,
  helperPdfService,
  liabilitiesService,
  CreditCardType
) => {
  /**
   * Prepare mortgage liability details.
   *
   * @param {Object} liability
   * @param {Array} contacts
   * @param {Array} assets
   * @param {Array} lenders
   * @param {Number} count
   *
   * @returns {Array}
   */
  const prepareMortgageLiabilityDetails = ({ liability, contacts, assets, lenders, count }) => {
    const content = [];
    content.push(
      helperPdfService.createSubHeader({
        title: `${liability.liabilityType.title.toUpperCase()} #${count}`
      })
    );
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Lender\n', style: 'label' },
                {
                  text: helperService.getLenderByContentType({
                    lenders,
                    lenderAbbr: liability?.name,
                    lenderOtherName: liability?.lenderOtherName,
                    contentType: 'abbr'
                  }),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'BSB\n', style: 'label' },
                {
                  text: liability?.bsb || '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Account number\n', style: 'label' },
                {
                  text: liability?.accountNumber || '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Interest rate\n', style: 'label' },
                {
                  text: `${liability?.interestRate.toFixed(2)}%`,
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                { text: 'Mortgage type\n', style: 'label' },
                {
                  text: liability?.mortgageType ? 'Investment' : 'Owner occupied',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Limit\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.limit, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Balance\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.balance, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Repayment monthly\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.repayment, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                {
                  text: 'Loan term expiry date\n',
                  style: 'label'
                },
                {
                  text: liability.getEndDate(
                    $rootScope.CurrentTimeZone(),
                    $rootScope.CurrentLocale()
                  ),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Repayment type\n', style: 'label' },
                {
                  text: liability?.repaymentType ? 'Interest only' : 'Principal and interest',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Fixed expiry date\n',
                  style: 'label'
                },
                {
                  text: liability.getFixedExpiryDate(
                    $rootScope.CurrentTimeZone(),
                    $rootScope.CurrentLocale()
                  ),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Fixed expiry date\n',
                  style: 'label'
                },
                {
                  text: liability.getInterestOnlyExpiryDate(
                    $rootScope.CurrentTimeZone(),
                    $rootScope.CurrentLocale()
                  ),
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                {
                  text: 'Refinance\n',
                  style: 'label'
                },
                {
                  text: liability?.isRefinanced ? 'Yes' : 'No',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Linked asset\n',
                  style: 'label'
                },
                {
                  text: helperService.getLinkedAsset({
                    assets,
                    idAsset: liability?.idAsset
                  }),
                  style: 'value'
                }
              ],
              colSpan: 3
            },
            '',
            ''
          ],
          [
            {
              text: [
                {
                  text: 'Ownership\n',
                  style: 'label'
                },
                {
                  text: helperService.getOwnerships({
                    contacts,
                    ownership: liability.ownership
                  }),
                  style: 'value'
                }
              ],
              colSpan: 4
            },
            '',
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });
    return content;
  };

  /**
   * Prepare credit card liability details.
   *
   * @param {Object} liability
   * @param {Array} contacts
   * @param {Array} lenders
   * @param {Number} count
   *
   * @returns {Array}
   */
  const prepareCreditCardLiabilityDetails = ({ liability, contacts, lenders, count }) => {
    const content = [];
    content.push(
      helperPdfService.createSubHeader({
        title: `${liability.liabilityType.title.toUpperCase()} #${count}`
      })
    );
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Lender\n', style: 'label' },
                {
                  text: helperService.getLenderByContentType({
                    lenders,
                    lenderAbbr: liability?.name,
                    lenderOtherName: liability?.lenderOtherName,
                    contentType: 'abbr'
                  }),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Credit card type\n', style: 'label' },
                {
                  text:
                    liability?.creditCardType?.name !== CreditCardType.none.name
                      ? liability?.creditCardType?.title || '-'
                      : '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Limit\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.limit, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Balance\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.balance, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                {
                  text: 'Refinance\n',
                  style: 'label'
                },
                {
                  text: liability?.isRefinanced ? 'Yes' : 'No',
                  style: 'value'
                }
              ]
            },
            '',
            '',
            ''
          ],
          [
            {
              text: [
                {
                  text: 'Ownership\n',
                  style: 'label'
                },
                {
                  text: helperService.getOwnerships({
                    contacts,
                    ownership: liability.ownership
                  }),
                  style: 'value'
                }
              ],
              colSpan: 4
            },
            '',
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });
    return content;
  };

  /**
   * Prepare overdraft liability details.
   *
   * @param {Object} liability
   * @param {Array} contacts
   * @param {Array} lenders
   * @param {Number} count
   *
   * @returns {Array}
   */
  const prepareOverdraftLiabilityDetails = ({ liability, contacts, lenders, count }) => {
    const content = [];
    content.push(
      helperPdfService.createSubHeader({
        title: `${liability.liabilityType.title.toUpperCase()} #${count}`
      })
    );
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Lender\n', style: 'label' },
                {
                  text: helperService.getLenderByContentType({
                    lenders,
                    lenderAbbr: liability?.name,
                    lenderOtherName: liability?.lenderOtherName,
                    contentType: 'abbr'
                  }),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Limit\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.limit, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Balance\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.balance, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Refinance\n',
                  style: 'label'
                },
                {
                  text: liability?.isRefinanced ? 'Yes' : 'No',
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                {
                  text: 'Ownership\n',
                  style: 'label'
                },
                {
                  text: helperService.getOwnerships({
                    contacts,
                    ownership: liability.ownership
                  }),
                  style: 'value'
                }
              ],
              colSpan: 4
            },
            '',
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });
    return content;
  };

  /**
   * Prepare vehicle liability details.
   *
   * @param {Object} liability
   * @param {Array} contacts
   * @param {Array} assets
   * @param {Array} lenders
   * @param {Number} count
   *
   * @returns {Array}
   */
  const prepareVehicleLiabilityDetails = ({ liability, contacts, assets, lenders, count }) => {
    const content = [];
    content.push(
      helperPdfService.createSubHeader({
        title: `${liability.liabilityType.title.toUpperCase()} #${count}`
      })
    );
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Lender\n', style: 'label' },
                {
                  text: helperService.getLenderByContentType({
                    lenders,
                    lenderAbbr: liability?.name,
                    lenderOtherName: liability?.lenderOtherName,
                    contentType: 'abbr'
                  }),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Interest rate\n', style: 'label' },
                {
                  text: `${liability?.interestRate.toFixed(2)}%`,
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Net amount financed\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.limit, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Balance\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.balance, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                { text: 'Repayment monthly\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.repayment, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Loan term expiry date\n',
                  style: 'label'
                },
                {
                  text: liability.getEndDate(
                    $rootScope.CurrentTimeZone(),
                    $rootScope.CurrentLocale()
                  ),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Refinance\n',
                  style: 'label'
                },
                {
                  text: liability?.isRefinanced ? 'Yes' : 'No',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Linked asset\n',
                  style: 'label'
                },
                {
                  text: helperService.getLinkedAsset({
                    assets,
                    idAsset: liability?.idAsset
                  }),
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                {
                  text: 'Ownership\n',
                  style: 'label'
                },
                {
                  text: helperService.getOwnerships({
                    contacts,
                    ownership: liability.ownership
                  }),
                  style: 'value'
                }
              ],
              colSpan: 4
            },
            '',
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });
    return content;
  };

  /**
   * Prepare personal liability details.
   *
   * @param {Object} liability
   * @param {Array} contacts
   * @param {Array} lenders
   * @param {Number} count
   *
   * @returns {Array}
   */
  const preparePersonalLiabilityDetails = ({ liability, contacts, lenders, count }) => {
    const content = [];
    content.push(
      helperPdfService.createSubHeader({
        title: `${liability.liabilityType.title.toUpperCase()} #${count}`
      })
    );
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Lender\n', style: 'label' },
                {
                  text: helperService.getLenderByContentType({
                    lenders,
                    lenderAbbr: liability?.name,
                    lenderOtherName: liability?.lenderOtherName,
                    contentType: 'abbr'
                  }),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'BSB\n', style: 'label' },
                {
                  text: liability?.bsb || '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Account number\n', style: 'label' },
                {
                  text: liability?.accountNumber || '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Interest rate\n', style: 'label' },
                {
                  text: `${liability?.interestRate.toFixed(2)}%`,
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                { text: 'Limit\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.limit, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Balance\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.balance, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Repayment monthly\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.repayment, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Loan term expiry date\n',
                  style: 'label'
                },
                {
                  text: liability.getEndDate(
                    $rootScope.CurrentTimeZone(),
                    $rootScope.CurrentLocale()
                  ),
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                {
                  text: 'Refinance\n',
                  style: 'label'
                },
                {
                  text: liability?.isRefinanced ? 'Yes' : 'No',
                  style: 'value'
                }
              ]
            },
            '',
            '',
            ''
          ],
          [
            {
              text: [
                {
                  text: 'Ownership\n',
                  style: 'label'
                },
                {
                  text: helperService.getOwnerships({
                    contacts,
                    ownership: liability.ownership
                  }),
                  style: 'value'
                }
              ],
              colSpan: 4
            },
            '',
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });
    return content;
  };

  /**
   * Prepare SMSF liability details.
   *
   * @param {Object} liability
   * @param {Array} contacts
   * @param {Array} assets
   * @param {Array} lenders
   * @param {Number} count
   *
   * @returns {Array}
   */
  const prepareSmsfLiabilityDetails = ({ liability, contacts, assets, lenders, count }) => {
    const content = [];
    content.push(
      helperPdfService.createSubHeader({
        title: `${liability.liabilityType.title.toUpperCase()} #${count}`
      })
    );
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Lender\n', style: 'label' },
                {
                  text: helperService.getLenderByContentType({
                    lenders,
                    lenderAbbr: liability?.name,
                    lenderOtherName: liability?.lenderOtherName,
                    contentType: 'abbr'
                  }),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'BSB\n', style: 'label' },
                {
                  text: liability?.bsb || '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Account number\n', style: 'label' },
                {
                  text: liability?.accountNumber || '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Interest rate\n', style: 'label' },
                {
                  text: `${liability?.interestRate.toFixed(2)}%`,
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                { text: 'Limit\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.limit, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Balance\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.balance, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Repayment monthly\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.repayment, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Loan term expiry date\n',
                  style: 'label'
                },
                {
                  text: liability.getEndDate(
                    $rootScope.CurrentTimeZone(),
                    $rootScope.CurrentLocale()
                  ),
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                { text: 'Repayment type\n', style: 'label' },
                {
                  text: liability?.repaymentType ? 'Interest only' : 'Principal and interest',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Fixed expiry date\n',
                  style: 'label'
                },
                {
                  text: liability.getFixedExpiryDate(
                    $rootScope.CurrentTimeZone(),
                    $rootScope.CurrentLocale()
                  ),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Refinance\n',
                  style: 'label'
                },
                {
                  text: liability?.isRefinanced ? 'Yes' : 'No',
                  style: 'value'
                }
              ]
            },
            ''
          ],
          [
            {
              text: [
                {
                  text: 'Linked asset\n',
                  style: 'label'
                },
                {
                  text: helperService.getLinkedAsset({
                    assets,
                    idAsset: liability?.idAsset
                  }),
                  style: 'value'
                }
              ],
              colSpan: 3
            },
            '',
            '',
            ''
          ],
          [
            {
              text: [
                {
                  text: 'Ownership\n',
                  style: 'label'
                },
                {
                  text: helperService.getOwnerships({
                    contacts,
                    ownership: liability.ownership
                  }),
                  style: 'value'
                }
              ],
              colSpan: 4
            },
            '',
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });
    return content;
  };

  /**
   * Prepare education debt liability details.
   *
   * @param {Object} liability
   * @param {Array} contacts
   * @param {Number} count
   *
   * @returns {Array}
   */
  const prepareEducationDebtLiabilityDetails = ({ liability, contacts, count }) => {
    const content = [];
    content.push(
      helperPdfService.createSubHeader({
        title: `${liability.liabilityType.title.toUpperCase()} #${count}`
      })
    );
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Details\n', style: 'label' },
                {
                  text: liability?.name || '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Balance\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.balance, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Repayment monthly\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.repayment, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Refinance\n',
                  style: 'label'
                },
                {
                  text: liability?.isRefinanced ? 'Yes' : 'No',
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                {
                  text: 'Ownership\n',
                  style: 'label'
                },
                {
                  text: helperService.getOwnerships({
                    contacts,
                    ownership: liability.ownership
                  }),
                  style: 'value'
                }
              ],
              colSpan: 4
            },
            '',
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });
    return content;
  };

  /**
   * Prepare other liability details.
   *
   * @param {Object} liability
   * @param {Array} contacts
   * @param {Array} lenders
   * @param {Number} count
   *
   * @returns {Array}
   */
  const prepareOtherLiabilityDetails = ({ liability, contacts, lenders, count }) => {
    const content = [];
    content.push(
      helperPdfService.createSubHeader({
        title: `${liability.liabilityType.title.toUpperCase()} #${count}`
      })
    );
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Lender\n', style: 'label' },
                {
                  text: helperService.getLenderByContentType({
                    lenders,
                    lenderAbbr: liability?.name,
                    lenderOtherName: liability?.lenderOtherName,
                    contentType: 'abbr'
                  }),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'BSB\n', style: 'label' },
                {
                  text: liability?.bsb || '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Account number\n', style: 'label' },
                {
                  text: liability?.accountNumber || '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Interest rate\n', style: 'label' },
                {
                  text: `${liability?.interestRate.toFixed(2)}%`,
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                { text: 'Limit\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.limit, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Balance\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.balance, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Repayment monthly\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.repayment, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                {
                  text: 'Loan term expiry date\n',
                  style: 'label'
                },
                {
                  text: liability.getEndDate(
                    $rootScope.CurrentTimeZone(),
                    $rootScope.CurrentLocale()
                  ),
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                {
                  text: 'Refinance\n',
                  style: 'label'
                },
                {
                  text: liability?.isRefinanced ? 'Yes' : 'No',
                  style: 'value'
                }
              ]
            },
            '',
            '',
            ''
          ],
          [
            {
              text: [
                {
                  text: 'Ownership\n',
                  style: 'label'
                },
                {
                  text: helperService.getOwnerships({
                    contacts,
                    ownership: liability.ownership
                  }),
                  style: 'value'
                }
              ],
              colSpan: 4
            },
            '',
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });
    return content;
  };

  /**
   * Prepare balance sheet liability details.
   *
   * @param {Object} liability
   * @param {Array} contacts
   * @param {Array} assets
   * @param {Array} lenders
   * @param {Number} count
   *
   * @returns {Array}
   */
  const prepareBalanceSheetLiabilityDetails = ({ liability, contacts, count }) => {
    const content = [];
    content.push(
      helperPdfService.createSubHeader({
        title: `${liability.liabilityType.title.toUpperCase()} #${count}`
      })
    );
    content.push({
      table: {
        widths: ['25%', '25%', '25%', '25%'],
        body: [
          [
            {
              text: [
                { text: 'Name\n', style: 'label' },
                {
                  text: liability?.name || '-',
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Balance\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.balance, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'Repayment monthly\n', style: 'label' },
                {
                  text: helperService
                    .isNumber(liability?.repayment, true)
                    .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                  style: 'value'
                }
              ]
            },
            {
              text: [
                { text: 'As at date\n', style: 'label' },
                {
                  text: liability.getAsAtDate(
                    $rootScope.CurrentTimeZone(),
                    $rootScope.CurrentLocale()
                  ),
                  style: 'value'
                }
              ]
            }
          ],
          [
            {
              text: [
                {
                  text: 'Ownership\n',
                  style: 'label'
                },
                {
                  text: helperService.getOwnerships({
                    contacts,
                    ownership: liability.ownership
                  }),
                  style: 'value'
                }
              ],
              colSpan: 4
            },
            '',
            '',
            ''
          ]
        ]
      },
      layout: 'noBorders'
    });
    return content;
  };

  /**
   * Prepare liabilities details.
   *
   * @param {Array} liabilities
   * @param {Array} contacts
   * @param {Array} assets
   * @param {Array} lenders
   *
   * @returns {Array}
   */
  const prepareLiabilitiesDetails = ({ liabilities, contacts, assets, lenders }) => {
    const content = [];
    if (liabilities?.length) {
      content.push({
        text: '',
        margin: [0, 20, 0, 0]
      });
      content.push(
        helperPdfService.createHeader({
          title: 'LIABILITIES'
        })
      );
      let mortgageLoanCount = 0;
      let creditCardCount = 0;
      let overdraftCount = 0;
      let vehicleLoanCount = 0;
      let personalLoanCount = 0;
      let smsfLoanCount = 0;
      let educationDebtCount = 0;
      let otherCount = 0;
      let balanceSheetCount = 0;
      const len = liabilities.length;
      for (let l = 0; l < len; l += 1) {
        const liability = liabilities[l];
        if (helperService.isLiabilityTypeMortgage(liability.liabilityType)) {
          mortgageLoanCount += 1;
          content.push(
            prepareMortgageLiabilityDetails({
              liability,
              contacts,
              assets,
              lenders,
              count: mortgageLoanCount
            })
          );
        }
        if (helperService.isLiabilityTypeCreditCard(liability.liabilityType)) {
          creditCardCount += 1;
          content.push(
            prepareCreditCardLiabilityDetails({
              liability,
              contacts,
              lenders,
              count: creditCardCount
            })
          );
        }
        if (helperService.isLiabilityTypeOverdraft(liability.liabilityType)) {
          overdraftCount += 1;
          content.push(
            prepareOverdraftLiabilityDetails({
              liability,
              contacts,
              lenders,
              count: overdraftCount
            })
          );
        }
        if (helperService.isLiabilityTypeVehicle(liability.liabilityType)) {
          vehicleLoanCount += 1;
          content.push(
            prepareVehicleLiabilityDetails({
              liability,
              contacts,
              assets,
              lenders,
              count: vehicleLoanCount
            })
          );
        }
        if (helperService.isLiabilityTypePersonal(liability.liabilityType)) {
          personalLoanCount += 1;
          content.push(
            preparePersonalLiabilityDetails({
              liability,
              contacts,
              lenders,
              count: personalLoanCount
            })
          );
        }
        if (helperService.isLiabilityTypeSmsf(liability.liabilityType)) {
          smsfLoanCount += 1;
          content.push(
            prepareSmsfLiabilityDetails({
              liability,
              contacts,
              assets,
              lenders,
              count: smsfLoanCount
            })
          );
        }
        if (helperService.isLiabilityTypeEducationDebt(liability.liabilityType)) {
          educationDebtCount += 1;
          content.push(
            prepareEducationDebtLiabilityDetails({
              liability,
              contacts,
              lenders,
              count: educationDebtCount
            })
          );
        }
        if (helperService.isLiabilityTypeOther(liability.liabilityType)) {
          otherCount += 1;
          content.push(
            prepareOtherLiabilityDetails({
              liability,
              contacts,
              lenders,
              count: otherCount
            })
          );
        }
        if (helperService.isLiabilityTypeBalanceSheet(liability.liabilityType)) {
          balanceSheetCount += 1;
          content.push(
            prepareOtherLiabilityDetails({
              liability,
              contacts,
              lenders,
              count: balanceSheetCount
            })
          );
        }
      }

      content.push(helperPdfService.createSubHeader({ title: 'TOTAL LIABILITIES' }));
      content.push({
        table: {
          widths: ['25%', '25%', '25%', '25%'],
          body: [
            [
              {
                text: [
                  {
                    text: 'Total balances\n',
                    style: 'label'
                  },
                  {
                    text: liabilitiesService
                      .getTotalLiabilitiesBalance(liabilities)
                      .formatWithCurrency($rootScope.CurrentCurrency(), 0),
                    style: 'value'
                  }
                ],
                colSpan: 2
              },
              '',
              '',
              ''
            ]
          ]
        },
        layout: 'noBorders'
      });
    }
    return content;
  };

  return {
    prepareLiabilitiesDetails,
    prepareMortgageLiabilityDetails,
    prepareCreditCardLiabilityDetails,
    prepareOverdraftLiabilityDetails,
    prepareVehicleLiabilityDetails,
    preparePersonalLiabilityDetails,
    prepareSmsfLiabilityDetails,
    prepareEducationDebtLiabilityDetails,
    prepareOtherLiabilityDetails,
    prepareBalanceSheetLiabilityDetails
  };
};
